import React, { useEffect, useState } from "react";
import { PhoneCall, HeartHandshake, CalendarHeart, Stethoscope, AlertTriangle, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { fmtDay, fmtTime } from "../../lib/api";
import { Loading, Logo } from "../../components/Shared";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

export default function SeniorMode() {
  const [d, setD] = useState(null);
  const [confirmSos, setConfirmSos] = useState(false);
  const [sosSent, setSosSent] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { api.get("/senior/home").then((r) => setD(r.data)).catch(() => setD({ empty: true })); }, []);
  if (!d) return <Loading label="One moment…" />;

  const sendSos = async () => {
    try {
      const r = await api.post("/senior/sos");
      setSosSent(true);
      setConfirmSos(false);
      toast.success(r.data.message);
    } catch { toast.error("Please call +91 86905 45601 directly."); }
  };

  const tile = "flex flex-col items-center justify-center gap-3 rounded-3xl p-6 text-white shadow-lg transition-transform active:scale-95 min-h-[140px]";
  const cm = d.care_manager;
  const fam = d.family_contact;

  return (
    <div className="min-h-screen bg-sand" data-testid="senior-mode">
      <header className="flex items-center justify-between border-b border-stone-200 bg-white px-4 py-3">
        <Logo />
        <div className="flex items-center gap-2">
          <button onClick={() => navigate("/app/family")} className="rounded-full border border-stone-200 px-3 py-1.5 text-xs font-semibold text-stone-500" data-testid="senior-exit-btn">Family view</button>
          <button onClick={() => { logout(); navigate("/"); }} className="rounded-full p-2 text-stone-400" aria-label="Log out" data-testid="senior-logout"><LogOut className="h-4 w-4" /></button>
        </div>
      </header>
      <main className="mx-auto max-w-md px-4 py-6">
        <h1 className="font-heading text-2xl font-bold text-stone-900" data-testid="senior-greeting">
          Namaste{d.parent ? `, ${d.parent.name.replace("Mrs. ", "").replace("Mr. ", "").split(" ")[0]} ji` : ""}!
        </h1>
        <p className="mt-1 text-lg text-stone-500">How can we help you today?</p>

        <div className="mt-6 grid grid-cols-2 gap-4">
          {cm?.phone ? (
            <a href={`tel:${cm.phone.replace(/\s/g, "")}`} className={`${tile} bg-emerald-800`} data-testid="senior-call-cm">
              <PhoneCall className="h-9 w-9" /><span className="text-center text-xl font-bold leading-tight">Call My Care Manager</span>
              <span className="text-sm opacity-80">{cm.name}</span>
            </a>
          ) : (
            <div className={`${tile} bg-stone-400`} data-testid="senior-call-cm-pending">
              <PhoneCall className="h-9 w-9" /><span className="text-center text-xl font-bold leading-tight">Call My Care Manager</span>
              <span className="text-center text-sm font-medium opacity-90">Contact details will appear once your Care Manager is assigned.</span>
            </div>
          )}
          {fam?.phone ? (
            <a href={`tel:${fam.phone.replace(/\s/g, "")}`} className={`${tile} bg-rose-700`} data-testid="senior-call-family">
              <HeartHandshake className="h-9 w-9" /><span className="text-center text-xl font-bold leading-tight">Call Family</span>
              <span className="text-sm opacity-80">{fam.name}</span>
            </a>
          ) : (
            <div className={`${tile} bg-stone-400`} data-testid="senior-call-family-pending">
              <HeartHandshake className="h-9 w-9" /><span className="text-center text-xl font-bold leading-tight">Call Family</span>
              <span className="text-center text-sm font-medium opacity-90">Your family's contact number will appear here once added.</span>
            </div>
          )}
          <div className={`${tile} bg-teal-800`} data-testid="senior-today-plan">
            <CalendarHeart className="h-9 w-9" /><span className="text-center text-xl font-bold leading-tight">Today's Plan</span>
            <span className="text-center text-sm opacity-80">
              {d.upcoming_visits?.length ? `${fmtDay(d.upcoming_visits[0].scheduled_at)} ${fmtTime(d.upcoming_visits[0].scheduled_at)} — ${d.upcoming_visits[0].purpose}` : "No visits planned today"}
            </span>
          </div>
          <div className={`${tile} bg-amber-700`} data-testid="senior-appointments">
            <Stethoscope className="h-9 w-9" /><span className="text-center text-xl font-bold leading-tight">My Appointments</span>
            <span className="text-center text-sm opacity-80">
              {d.appointments?.length ? `${d.appointments[0].title} — ${fmtDay(d.appointments[0].when)}` : "Nothing scheduled"}
            </span>
          </div>
        </div>

        {sosSent ? (
          <div className="mt-6 rounded-3xl border-2 border-emerald-700 bg-emerald-50 p-6 text-center" data-testid="sos-sent">
            <p className="font-heading text-xl font-bold text-emerald-900">Help is on the way.</p>
            <p className="mt-2 text-stone-600">Our team has been alerted and your family has been informed. Stay calm — someone will call you right away.</p>
            <button onClick={() => setSosSent(false)} className="mt-4 rounded-full border border-stone-300 px-5 py-2 text-sm font-semibold" data-testid="sos-dismiss">OK</button>
          </div>
        ) : confirmSos ? (
          <div className="mt-6 rounded-3xl border-2 border-red-500 bg-red-50 p-6 text-center" data-testid="sos-confirm">
            <AlertTriangle className="mx-auto h-10 w-10 text-red-600" />
            <p className="mt-2 font-heading text-xl font-bold text-red-800">Do you need urgent help?</p>
            <p className="mt-1 text-sm text-red-700">Pressing YES will alert the ParentOne team and your family immediately. For a life-threatening emergency, call 112 first.</p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <button onClick={() => setConfirmSos(false)} className="rounded-2xl border-2 border-stone-300 bg-white py-4 text-lg font-bold text-stone-700" data-testid="sos-no">NO, go back</button>
              <button onClick={sendSos} className="rounded-2xl bg-red-600 py-4 text-lg font-bold text-white" data-testid="sos-yes">YES, I need help</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setConfirmSos(true)} className="mt-6 flex w-full items-center justify-center gap-3 rounded-3xl bg-red-600 py-7 text-white shadow-lg transition-transform active:scale-95" data-testid="senior-sos-btn">
            <AlertTriangle className="h-9 w-9" /><span className="text-2xl font-extrabold tracking-wide">EMERGENCY SOS</span>
          </button>
        )}

        <p className="mt-6 text-center text-xs leading-relaxed text-stone-400">
          ParentOne emergency coordination is not a replacement for government emergency services. In a life-threatening emergency, call <strong>112</strong> or <strong>108</strong> first.
        </p>
      </main>
    </div>
  );
}
